// ============================================================================
// useFinanceActions.js - V17 ENTERPRISE (DATA CONTEXT ULTRA-ROBUST)
// SALVAR, DELETAR, EXPORTAR, COMPARTILHAR
// DataContext (fuente única de verdad) + DualSyncStrategy V14 (offline/online)
//
// 🔥 V17: handleSave/executeDelete → notifyLocalChange INMEDIATO + refreshFinanceData
// 🔥 V17: onDataChanged callback post-operación para refresco en tiempo real
// 🔥 V16: getContext ASYNC — espera la instancia de DataContext correctamente
// ============================================================================
import { useState, useCallback, useRef } from "react";
import { exportFinancePDF } from "../../services/financeExport";
import { exportSingleFinancePDF } from "../services/pdfExport";
import DataContext from "../../lib/data/context/DataContext";

export const useFinanceActions = (
  database,
  setRegistros,
  company,
  user,
  showToast,
  addNotification,
  notifyLocalChange,
  getContext = null,
  onDataChanged = null  // 🔥 V17: Callback post-operación para refresco UI
) => {
  // ── Estados ───────────────────────────────────────────────────────────────
  const [showModal,      setShowModal]      = useState(false);
  const [confirmDelete,  setConfirmDelete]  = useState({ isOpen: false, id: null });
  const [form,           setForm]           = useState({
    type:      "receita",
    categoria: "",
    valor:     "",
    data:      "",
    descricao: "",
  });

  const registrosRef = useRef([]);
  const dataContextRef = useRef(null);

  // ── Sync ref ──────────────────────────────────────────────────────────────
  const syncRef = useCallback((registros) => {
    registrosRef.current = Array.isArray(registros) ? registros : [];
  }, []);

  // ── OBTENER DATACONTEXT (ASYNC, CON CACHE) ────────────────────────────────
  const getDataContext = useCallback(async () => {
    if (dataContextRef.current) {
      return dataContextRef.current;
    }

    if (typeof getContext === 'function') {
      try {
        const ctx = await getContext();
        if (ctx && ctx.collections) {
          dataContextRef.current = ctx;
          return ctx;
        }
      } catch (e) {
        console.warn('⚠️ [V17] getContext externo falló:', e.message);
      }
    }

    if (user?.uid) {
      try {
        const ctx = DataContext.getInstance(user.uid);
        if (ctx && ctx.collections) {
          dataContextRef.current = ctx;
          return ctx;
        }
      } catch (e) {
        console.warn('⚠️ [V17] DataContext.getInstance falló:', e.message);
      }
    }

    return null;
  }, [getContext, user?.uid]);

  // ── REFRESCAR DATOS FINANCIEROS ───────────────────────────────────────────
  const refreshFinanceData = useCallback(async () => {
    try {
      const ctx = await getDataContext();
      
      if (ctx?.collections?.financeiro?.read) {
        const freshData = await ctx.collections.financeiro.read();
        if (Array.isArray(freshData)) {
          setRegistros(freshData);
          registrosRef.current = freshData;
          return freshData;
        }
      }
      
      if (database?.getFinanceiro) {
        const freshData = await database.getFinanceiro();
        if (Array.isArray(freshData)) {
          setRegistros(freshData);
          registrosRef.current = freshData;
        }
        return freshData;
      }
    } catch (error) {
      console.warn('⚠️ [V17] refreshFinanceData falló:', error.message);
    }
    return registrosRef.current;
  }, [getDataContext, database, setRegistros]);

  // ── 🔥 V17: SALVAR CON REFRESCO INMEDIATO ────────────────────────────────
  const handleSave = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const formData = form["data"] || form.data || "";
    if (!form.valor || !formData || !form.categoria) {
      showToast("Preencha categoria, valor e data", "error", {
        title: "Campos obrigatórios",
      });
      return;
    }

    // 🔥 V18: valor numérico seguro + fecha normalizada YYYY-MM-DD
    const rawValor = Number(String(form.valor).replace(",", "."));
    const valorNum = isFinite(rawValor) && rawValor >= 0 ? rawValor : NaN;
    if (!isFinite(valorNum) || valorNum <= 0) {
      showToast("Informe um valor válido maior que zero", "error", {
        title: "Valor inválido",
      });
      return;
    }

    const dateNorm = String(formData).slice(0, 10);

    const registroData = {
      type:        form.type === "despesa" ? "despesa" : "receita",
      category:    form.categoria,
      value:       valorNum,
      valor:       valorNum, // compat legacy
      date:        dateNorm,
      data:        dateNorm, // compat legacy
      title:       form.descricao || form.categoria,
      description: form.descricao,
      userId:      user?.uid || 'anonymous',
      companyId:   user?.companyId || user?.company || 'default',
      createdAt:   new Date().toISOString(),
      status:      "active",
    };

    let novoRegistro = null;
    let usadoDataContext = false;

    const ctx = await getDataContext();
    
    if (ctx?.collections?.financeiro?.create) {
      try {
        novoRegistro = await ctx.collections.financeiro.create(registroData);
        usadoDataContext = true;
        console.log('✅ [V17] Registro creado vía DataContext:', novoRegistro?.id);
      } catch (dataContextError) {
        console.error('❌ [V17] DataContext create falló:', dataContextError.message);
        showToast("Erro ao salvar registro. Tente novamente.", "error", {
          title: "❌ Falha no banco de dados"
        });
        return;
      }
    }

    if (!usadoDataContext && database?.createFinanceiro) {
      try {
        novoRegistro = await database.createFinanceiro(registroData);
        console.log('⚠️ [V17] Registro creado vía database.js (fallback):', novoRegistro?.id);
      } catch (dbError) {
        console.error('❌ [V17] database.js create falló:', dbError.message);
        showToast("Erro ao salvar registro", "error", { title: "❌ Falha no banco de dados" });
        return;
      }
    }

    if (!novoRegistro || !novoRegistro.id) {
      console.error("❌ [V17] Registro creado sin ID:", novoRegistro);
      showToast("Erro ao criar registro", "error", { title: "Falha no banco de dados" });
      return;
    }

    // 🔥 V17: PASO 1 — Notificar cambio INMEDIATO (para polling cross-tab)
    if (typeof notifyLocalChange === "function") {
      notifyLocalChange();
    }

    // 🔥 V17: PASO 2 — Refresco completo para consistencia
    await refreshFinanceData();

    // 🔥 V17: PASO 3 — Callback para refrescar UI del padre
    if (typeof onDataChanged === "function") {
      onDataChanged();
    }

    // Feedback
    const valorFormatado = valorNum.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    showToast(`${form.categoria}: R$ ${valorFormatado}`, "success", {
      title: form.type === "receita" ? "💰 Receita adicionada" : "💸 Despesa adicionada",
    });

    addNotification({
      type:    "success",
      title:   form.type === "receita" ? "Nova receita" : "Nova despesa",
      message: `${form.categoria} · R$ ${valorFormatado}`,
      metadata: {
        category:  form.categoria,
        value:     valorNum,
        type:      form.type,
        date:      dateNorm,
        source:    usadoDataContext ? 'datacontext_v18' : 'database_js_fallback',
        timestamp: new Date().toISOString(),
      },
    });

    setShowModal(false);
    setForm({ type: "receita", categoria: "", valor: "", data: "", descricao: "" });
  }, [database, form, refreshFinanceData, notifyLocalChange, onDataChanged, showToast, addNotification, getDataContext, user]);

  // ── DELETAR CLICK ─────────────────────────────────────────────────────────
  const handleDeleteClick = useCallback((id) => {
    setConfirmDelete({ isOpen: true, id });
  }, []);

  // ── 🔥 V17: EXECUTAR DELETE CON REFRESCO INMEDIATO ────────────────────────
  const executeDelete = useCallback(async () => {
    const idToDelete = confirmDelete.id;
    if (!idToDelete) {
      setConfirmDelete({ isOpen: false, id: null });
      return;
    }

    const registro      = registrosRef.current.find((r) => r.id === idToDelete || r.uid === idToDelete);
    const valor         = registro?.value    || 0;
    const tipo          = registro?.type === "receita" ? "Receita" : "Despesa";
    const categoria     = registro?.category || "";
    const valorFormatado = Number(valor).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    let usadoDataContext = false;

    const ctx = await getDataContext();
    
    if (ctx?.collections?.financeiro?.delete) {
      try {
        await ctx.collections.financeiro.delete(idToDelete);
        usadoDataContext = true;
        console.log('✅ [V17] Registro eliminado vía DataContext:', idToDelete);
      } catch (dataContextError) {
        console.error('❌ [V17] DataContext delete falló:', dataContextError.message);
        showToast("Erro ao excluir registro", "error", { title: "❌ Falha no banco de dados" });
        setConfirmDelete({ isOpen: false, id: null });
        return;
      }
    }

    if (!usadoDataContext && database?.deleteFinanceiro) {
      try {
        await database.deleteFinanceiro(idToDelete);
        console.log('⚠️ [V17] Registro eliminado vía database.js (fallback):', idToDelete);
      } catch (dbError) {
        console.error('❌ [V17] database.js delete falló:', dbError.message);
        showToast("Erro ao excluir registro", "error", { title: "❌ Falha no banco de dados" });
        setConfirmDelete({ isOpen: false, id: null });
        return;
      }
    }

    // 🔥 V17: PASO 1 — Notificar cambio INMEDIATO
    if (typeof notifyLocalChange === "function") {
      notifyLocalChange();
    }

    // 🔥 V17: PASO 2 — Refresco completo
    await refreshFinanceData();

    // 🔥 V17: PASO 3 — Callback para refrescar UI del padre
    if (typeof onDataChanged === "function") {
      onDataChanged();
    }

    showToast(`${tipo} de R$ ${valorFormatado} (${categoria}) removida`, "info", {
      title: "🗑️ Lançamento excluído",
    });

    addNotification({
      type:    "info",
      title:   "Lançamento excluído",
      message: `${tipo} · ${categoria} · R$ ${valorFormatado}`,
      metadata: {
        category:  categoria,
        value:     valor,
        type:      registro?.type,
        source:    usadoDataContext ? 'datacontext_v17' : 'database_js_fallback',
        timestamp: new Date().toISOString(),
      },
    });

    setConfirmDelete({ isOpen: false, id: null });
  }, [confirmDelete.id, database, refreshFinanceData, notifyLocalChange, onDataChanged, showToast, addNotification, getDataContext]);

  const cancelDelete = useCallback(() => {
    setConfirmDelete({ isOpen: false, id: null });
  }, []);

  // ── EXPORTAR PDF ──────────────────────────────────────────────────────────
  const handleExportPDF = useCallback(async ({
    mes,
    registrosFiltrados,
    totalReceitas,
    totalDespesas,
    saldo,
    vendasStats,
    alugueisStats,
  }) => {
    showToast("Seu relatório financeiro está sendo preparado", "info", { title: "📄 Gerando PDF" });
    try {
      let userPrefs = {};
      const ctx = await getDataContext();
      
      if (ctx?.collections?.users?.read) {
        try {
          const userData = await ctx.collections.users.read({ id: user?.uid });
          if (userData?.pdfConfig) userPrefs = userData.pdfConfig;
        } catch (e) {}
      }
      
      if (Object.keys(userPrefs).length === 0 && database?.getUserPreferences) {
        try { const prefs = database.getUserPreferences(); userPrefs = prefs?.pdfConfig || {}; } catch (e) {}
      }

      await exportFinancePDF({
        mes,
        registros: registrosFiltrados, totalReceitas, totalDespesas, saldo,
        vendas: vendasStats, alugueis: alugueisStats,
        empresa: company?.nome || "Imóveis Pro",
        logoBase64: company?.logo || null,
        corPrimaria: company?.cor || "#2563eb",
        cnpj: company?.cnpj || "",
        telefone: company?.telefone || "",
        slogan: company?.slogan || "",
        mostrarMarcaDagua: company?.mostrarMarcaDagua !== false,
        usuario: user?.name || "Corretor",
        chartId: "finance-charts-pdf",
        pdfConfig: userPrefs,
      });
      showToast(`Relatório de ${mes} pronto para download`, "success", { title: "✅ PDF gerado" });
    } catch (error) {
      console.error("❌ Erro ao gerar PDF:", error);
      showToast(error.message || "Tente novamente mais tarde", "error", { title: "❌ Erro ao gerar PDF" });
    }
  }, [database, company, user, showToast, getDataContext]);

  const handleExportSinglePDF = useCallback((item) => {
    try {
      exportSingleFinancePDF(item, company, user?.name || "Corretor");
      showToast("PDF do lançamento gerado com sucesso", "success", { title: "📄 PDF individual" });
    } catch (error) {
      console.error("❌ Erro ao gerar PDF individual:", error);
      showToast("Erro ao gerar PDF do lançamento", "error", { title: "❌ Erro no PDF" });
    }
  }, [company, user, showToast]);

  const handleShareWhatsApp = useCallback((item) => {
    try {
      const tipo = item.type === "receita" ? "💰 Receita" : "💸 Despesa";
      const valorFormatado = Number(item.value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
      const dataFormatada = new Date((item.date || "") + "T12:00:00").toLocaleDateString("pt-BR");
      const mensagem = encodeURIComponent(
        `*${tipo} - ${item.category}*\n\n📅 Data: ${dataFormatada}\n💰 Valor: ${valorFormatado}\n📝 Descrição: ${item.description || item.title || "Sem descrição"}\n${item.propertyId ? `🏠 Imóvel: ${item.propertyId}\n` : ""}\nRegistro financeiro do sistema ${company?.nome || "IlhaCRM"}`
      );
      window.open(`https://wa.me/?text=${mensagem}`, "_blank");
    } catch (error) {
      console.error("❌ Erro ao compartilhar WhatsApp:", error);
    }
  }, [company]);

  return {
    showModal, setShowModal, confirmDelete, form, setForm, syncRef,
    handleSave, handleDeleteClick, executeDelete, cancelDelete,
    handleExportPDF, handleShareWhatsApp, handleExportSinglePDF,
    refreshFinanceData, getDataContext,
  };
};

export default useFinanceActions;